import { useState } from "react";
import { CreditCard, Wallet, Coins, ArrowRight, Check } from "lucide-react";
import { RAILS, startStripeCheckout, submitWeb3Intent } from "../lib/payments";
import { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const ICON_MAP = {
  card: CreditCard,
  stable: Coins,
  wallet: Wallet,
};

// Brand badges — visual reassurance that all rails are present
const BRAND_BADGES = {
  VISA: "VISA",
  MC: "MasterCard",
  AMEX: "AMEX",
  ACH: "ACH Bank Debit",
  CASHAPP: "Cash App Pay",
  KLARNA: "Klarna",
  APPLE: "Apple Pay",
  GOOGLE: "Google Pay",
  USDC: "USDC",
  NFT: "NFT Transfer",
  META: "MetaMask",
  WC: "WalletConnect",
  PHTM: "Phantom",
};

/**
 * Reusable payment-method picker.
 * Props:
 *  - packageId : server-defined package key (e.g. "franchise_grow_rich_upfront")
 *  - amount    : USD amount (display only — backend re-derives)
 *  - label     : display label (e.g. "WealthFirst Grow-Rich — Setup")
 *  - metadata  : object merged into transaction metadata (e.g. {franchise_id})
 *  - onIntent  : optional callback after successful intent record
 */
export default function PaymentMethodSelector({ packageId, amount, label, metadata = {}, onIntent }) {
  const [rail, setRail] = useState("stripe");
  const [busy, setBusy] = useState(false);
  const [web3Open, setWeb3Open] = useState(false);

  const handlePay = async () => {
    setBusy(true);
    try {
      if (rail === "stripe" || rail === "stripe_crypto") {
        await startStripeCheckout({ package_id: packageId, rail, metadata });
        return; // redirected
      }
      if (rail === "web3_wallet") {
        setWeb3Open(true);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start payment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="payment-method-selector" className="space-y-6">
      <div>
        <p className="eyebrow mb-3">Choose Payment Rail</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {RAILS.map((r) => {
            const Icon = ICON_MAP[r.icon] || CreditCard;
            const active = rail === r.id;
            return (
              <label
                key={r.id}
                data-testid={`rail-option-${r.id}`}
                className="p-4 cursor-pointer transition-all flex gap-3 items-start"
                style={{
                  border: active ? "1px solid var(--brass)" : "1px solid var(--hairline)",
                  background: active ? "var(--surface)" : "transparent",
                }}
              >
                <input
                  type="radio"
                  name="payment_rail"
                  value={r.id}
                  checked={active}
                  onChange={() => setRail(r.id)}
                  className="sr-only"
                />
                <Icon
                  size={22}
                  style={{ color: active ? "var(--brass)" : "var(--ink-muted)", flexShrink: 0, marginTop: 2 }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <p
                      className="text-xs uppercase tracking-[0.15em] font-semibold"
                      style={{ color: active ? "var(--brass)" : "var(--ink)" }}
                    >
                      {r.short}
                    </p>
                    {active && <Check size={14} style={{ color: "var(--brass)" }} />}
                  </div>
                  <p className="text-sm mb-2 leading-snug" style={{ color: "var(--ink)" }}>
                    {r.label}
                  </p>
                  <p className="text-xs leading-relaxed mb-3" style={{ color: "var(--ink-muted)" }}>
                    {r.description}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {r.methods.map((m) => (
                      <span
                        key={m}
                        className="text-[10px] uppercase tracking-[0.12em] px-2 py-0.5"
                        style={{
                          border: "1px solid var(--hairline-strong)",
                          color: active ? "var(--brass)" : "var(--ink-muted)",
                          background: active ? "rgba(194,163,115,0.05)" : "transparent",
                        }}
                      >
                        {BRAND_BADGES[m] || m}
                      </span>
                    ))}
                  </div>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      <div
        className="p-5"
        style={{ border: "1px solid var(--hairline)", background: "rgba(194,163,115,0.04)" }}
      >
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs uppercase tracking-[0.18em]" style={{ color: "var(--ink-muted)" }}>
            You are paying
          </span>
          <span className="font-display text-2xl" style={{ color: "var(--brass)" }}>
            ${Number(amount).toLocaleString()}
          </span>
        </div>
        <p className="text-xs leading-relaxed" style={{ color: "var(--ink-muted)" }}>
          {label} — settled via {RAILS.find((r) => r.id === rail)?.short}.
        </p>
      </div>

      <button
        type="button"
        onClick={handlePay}
        disabled={busy}
        className="btn-brass w-full"
        style={{ justifyContent: "center" }}
        data-testid="pay-now-button"
      >
        {busy ? "Preparing secure checkout…" : `Pay $${Number(amount).toLocaleString()} now`}
        <ArrowRight size={14} />
      </button>

      {web3Open && (
        <Web3IntentDialog
          packageId={packageId}
          amount={amount}
          label={label}
          metadata={metadata}
          onClose={() => setWeb3Open(false)}
          onSubmitted={(data) => {
            setWeb3Open(false);
            toast.success("Web3 / NFT intent recorded.");
            onIntent?.(data);
          }}
        />
      )}
    </div>
  );
}

function Web3IntentDialog({ packageId, amount, label, metadata, onClose, onSubmitted }) {
  const [form, setForm] = useState({
    wallet_address: "",
    chain: "ethereum",
    asset: "ETH",
    tx_hash: "",
    nft_contract: "",
    nft_token_id: "",
    note: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.wallet_address || form.wallet_address.length < 10) {
      toast.error("Enter a valid wallet address.");
      return;
    }
    setBusy(true);
    try {
      const data = await submitWeb3Intent({
        package_id: packageId,
        wallet_address: form.wallet_address,
        chain: form.chain,
        asset: form.asset,
        tx_hash: form.tx_hash,
        nft_contract: form.nft_contract,
        nft_token_id: form.nft_token_id,
        note: form.note,
        metadata,
      });
      onSubmitted?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not record intent.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)" }}
      data-testid="web3-intent-dialog"
    >
      <div
        className="w-full max-w-xl p-8 max-h-[90vh] overflow-y-auto"
        style={{ background: "var(--surface)", border: "1px solid var(--brass)" }}
      >
        <p className="eyebrow mb-2">Web3 / NFT / DeFi Payment Intent</p>
        <h2 className="font-display text-3xl mb-2 leading-tight">Pay with on-chain assets.</h2>
        <p className="text-sm mb-6" style={{ color: "var(--ink-muted)" }}>
          Send funds from MetaMask, Phantom, Trust, or any WalletConnect wallet — or transfer an NFT
          equivalent in value. Our concierge will verify on-chain settlement and email confirmation.
          You are paying <span style={{ color: "var(--brass)" }}>${Number(amount).toLocaleString()}</span> for <em>{label}</em>.
        </p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="eyebrow block mb-2">Sender Wallet Address *</label>
            <input
              required
              value={form.wallet_address}
              onChange={(e) => set("wallet_address", e.target.value)}
              placeholder="0x… or bc1q… or T… or PhantomPubkey"
              className="input-line"
              data-testid="web3-wallet-address"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="eyebrow block mb-2">Chain</label>
              <select
                value={form.chain}
                onChange={(e) => set("chain", e.target.value)}
                className="input-line"
                style={{ background: "var(--ink)", color: "var(--ink-light)" }}
                data-testid="web3-chain"
              >
                <option value="ethereum">Ethereum (Layer-1)</option>
                <option value="bitcoin">Bitcoin (Layer-1)</option>
                <option value="polygon">Polygon (Layer-2)</option>
                <option value="bsc">BNB Smart Chain</option>
                <option value="solana">Solana</option>
                <option value="tron">Tron (USDT-TRC20)</option>
              </select>
            </div>
            <div>
              <label className="eyebrow block mb-2">Asset *</label>
              <input
                required
                value={form.asset}
                onChange={(e) => set("asset", e.target.value)}
                placeholder="ETH · BTC · USDT · DOGE · SHIB · NFT:CryptoPunk #1234"
                className="input-line"
                data-testid="web3-asset"
              />
            </div>
          </div>
          <div>
            <label className="eyebrow block mb-2">Transaction Hash (if already sent)</label>
            <input
              value={form.tx_hash}
              onChange={(e) => set("tx_hash", e.target.value)}
              placeholder="0xabc… (optional — leave blank, concierge will share receiving wallet)"
              className="input-line"
              data-testid="web3-txhash"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="eyebrow block mb-2">NFT Contract (if paying with NFT)</label>
              <input
                value={form.nft_contract}
                onChange={(e) => set("nft_contract", e.target.value)}
                placeholder="0x…"
                className="input-line"
                data-testid="web3-nft-contract"
              />
            </div>
            <div>
              <label className="eyebrow block mb-2">NFT Token ID</label>
              <input
                value={form.nft_token_id}
                onChange={(e) => set("nft_token_id", e.target.value)}
                placeholder="e.g. 1234"
                className="input-line"
                data-testid="web3-nft-tokenid"
              />
            </div>
          </div>
          <div>
            <label className="eyebrow block mb-2">Notes</label>
            <textarea
              rows={3}
              value={form.note}
              onChange={(e) => set("note", e.target.value)}
              placeholder="Anything our concierge should know?"
              className="w-full bg-transparent p-3 text-base outline-none"
              style={{ border: "1px solid var(--hairline-strong)", color: "var(--ink)" }}
              data-testid="web3-note"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 text-sm uppercase tracking-[0.18em]"
              style={{ border: "1px solid var(--hairline-strong)", color: "var(--ink-muted)" }}
              data-testid="web3-cancel-button"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="btn-brass flex-1"
              style={{ justifyContent: "center" }}
              data-testid="web3-submit-button"
            >
              {busy ? "Recording…" : "Record Intent"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
